export class QuantumParticleAnalyzer {
  constructor() {
    this.particleThresholds = {
      electron: { minFreq: 1e12, maxFreq: 1e15 },
      photon: { minFreq: 1e14, maxFreq: 1e17 },
      quark: { minFreq: 1e20, maxFreq: 1e23 }
    };
    
    this.spinStates = {
      UP: 'spin-up',
      DOWN: 'spin-down',
      SUPERPOSITION: 'superposition'
    };
  }

  analyzeParticleProperties(metrics) {
    const {
      frequency,
      quantum_coherence,
      quantum_phase,
      quantum_energy
    } = metrics;

    return {
      particleType: this.identifyParticleType(frequency),
      spinState: this.determineSpinState(quantum_phase),
      decayProperties: this.analyzeDecay(quantum_energy),
      tunneling: this.calculateTunnelingProbability(metrics),
      waveParticleDuality: this.analyzeWaveParticleDuality(metrics)
    };
  }

  identifyParticleType(frequency) {
    for (const [type, range] of Object.entries(this.particleThresholds)) {
      if (frequency >= range.minFreq && frequency <= range.maxFreq) {
        return {
          type,
          confidence: this.calculateConfidence(frequency, range),
          energy: this.calculateParticleEnergy(frequency)
        };
      }
    }
    return { type: 'unknown', confidence: 0, energy: 0 };
  }

  determineSpinState(phase) {
    const normalizedPhase = phase % (2 * Math.PI);
    
    if (normalizedPhase < Math.PI / 4 || normalizedPhase > 7 * Math.PI / 4) {
      return {
        state: this.spinStates.UP,
        angle: (normalizedPhase * 180 / Math.PI).toFixed(2) + '°',
        probability: Math.cos(normalizedPhase) ** 2
      };
    } else if (normalizedPhase >= 3 * Math.PI / 4 && normalizedPhase <= 5 * Math.PI / 4) {
      return {
        state: this.spinStates.DOWN,
        angle: (normalizedPhase * 180 / Math.PI).toFixed(2) + '°',
        probability: Math.sin(normalizedPhase) ** 2
      };
    } else {
      return {
        state: this.spinStates.SUPERPOSITION,
        angle: (normalizedPhase * 180 / Math.PI).toFixed(2) + '°',
        probability: 0.5
      };
    }
  }

  analyzeDecay(energy) {
    const halfLife = this.calculateHalfLife(energy);
    const decayConstant = Math.log(2) / halfLife;
    
    return {
      halfLife: halfLife.toExponential(2) + ' s',
      decayConstant: decayConstant.toExponential(2) + ' s⁻¹',
      decayProbability: (1 - Math.exp(-decayConstant)).toFixed(4),
      stabilityIndex: this.calculateStabilityIndex(energy)
    };
  }

  calculateTunnelingProbability(metrics) {
    const { quantum_coherence, quantum_energy } = metrics;
    const barrierHeight = quantum_energy * 2;
    const tunnelFactor = Math.exp(-2 * Math.sqrt(2 * barrierHeight));
    
    return {
      probability: tunnelFactor.toExponential(3),
      barrierHeight: barrierHeight.toExponential(2) + ' eV',
      penetrationDepth: (1 / Math.sqrt(2 * barrierHeight)).toExponential(2) + ' m'
    };
  }

  analyzeWaveParticleDuality(metrics) {
    const { frequency, quantum_coherence } = metrics;
    const wavelength = 3e8 / frequency; // c/f
    const momentum = 6.626e-34 * frequency / 3e8; // h/λ
    
    return {
      wavelength: wavelength.toExponential(2) + ' m',
      momentum: momentum.toExponential(2) + ' kg⋅m/s',
      deBroglieRelation: (wavelength * momentum).toExponential(2) + ' J⋅s',
      coherenceLength: (wavelength * quantum_coherence).toExponential(2) + ' m'
    };
  }

  // Helper methods
  calculateConfidence(frequency, range) {
    const midpoint = (range.maxFreq + range.minFreq) / 2;
    const distance = Math.abs(frequency - midpoint);
    const maxDistance = (range.maxFreq - range.minFreq) / 2;
    return (1 - distance / maxDistance) * 100;
  }

  calculateParticleEnergy(frequency) {
    const planckConstant = 6.62607015e-34;
    return (planckConstant * frequency).toExponential(2) + ' J';
  }

  calculateHalfLife(energy) {
    // Simplified model based on energy levels
    return 1 / (energy * 1e-10);
  }

  calculateStabilityIndex(energy) {
    const threshold = 1e-10;
    return energy < threshold ? 'Stable' : 'Unstable';
  }
}

export default QuantumParticleAnalyzer;